import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, MenuItem, Select } from "@mui/material";
import API_BASE_URL from "../../../../Constants";

import ManageDevicesModal from "./ManageDevicesModal";
import ManageWorkItemsModal from "./ManageWorkItemsModal";

const buildingTypeOptions = [
    { key: "Job Tracking", label: "Job Tracking" },
    { key: "Pricing / Costing Only", label: "Pricing / Costing Only" },
    { key: "Both - tracking and costing", label: "Both - tracking and costing" }
];

const WorkPhaseModal = ({ open, handleClose, proposalNumber, buildingId, viewOnly = false }) => {
    const [phases, setPhases] = useState([]);
    const [allWorkItems, setAllWorkItems] = useState([]); // Store all available work items
    const [allBuildingDevices, setAllBuildingDevices] = useState([]); // Store all devices for the building
    const [selectedPhase, setSelectedPhase] = useState(null); // Store the selected phase for device management

    const [isManageDevicesOpen, setIsManageDevicesOpen] = useState(false);
    const [isManageWorkItemsOpen, setIsManageWorkItemsOpen] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isAddingPhase, setIsAddingPhase] = useState(false);
    const [newPhase, setNewPhase] = useState({
        phaseBuildingType: "Job Tracking",
        phaseIndex: 1,
        laborRate: "",
        materialMarkup: ""
    });



    // Fetch work phases for the given proposal
    useEffect(() => {
        if (open && proposalNumber && buildingId) {
            fetchPhases();
            fetchAllWorkItems();
            fetchBuildingDevices();
        }
    }, [open, proposalNumber, buildingId]);

    const fetchPhases = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/proposals-and-jobs/phases/proposal/${proposalNumber}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include"

                }

            );
            if (!response.ok) throw new Error("Failed to fetch phases");

            const data = await response.json();
            setPhases(data);

            // Determine the next available phase index
            const nextIndex = data.length > 0 ? Math.max(...data.map((p) => p.phaseIndex)) + 1 : 1;
            setNewPhase((prev) => ({ ...prev, phaseIndex: nextIndex }));

        } catch (error) {
            console.error("Error fetching phases:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchBuildingDevices = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/setup/buildings/devices/${buildingId}`);
            if (!response.ok) throw new Error("Failed to fetch building devices");

            const devices = await response.json();
            setAllBuildingDevices(devices);

        } catch (error) {
            console.error("Error fetching building devices:", error);
        }
    };


    // Fetch all available work items
    const fetchAllWorkItems = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/proposals-and-jobs/work-items`);
            if (!response.ok) throw new Error("Failed to fetch work items");
            const data = await response.json();

            // order by itemGroup length
            data.sort((a, b) => a.itemGroup.length - b.itemGroup.length);
            setAllWorkItems(data);
        } catch (error) {
            console.error("Error fetching work items:", error);
        }
    };

    // Handle input changes for new phase
    const handleInputChange = (e) => {
        setNewPhase({ ...newPhase, [e.target.name]: e.target.value });
    };

    // Submit new phase
    const handleAddPhase = async () => {
        if (!newPhase.laborRate || !newPhase.materialMarkup) {
            alert("Please fill all required fields!");
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/proposals-and-jobs/phases/proposal/${proposalNumber}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify([{ ...newPhase, proposal: { proposalNumber } }]), // API expects list
            });

            if (!response.ok) throw new Error("Failed to add phase");

            const addedPhases = await response.json();
            setPhases([...phases, ...addedPhases]);
            setIsAddingPhase(false);

            // Determine the next available phase index for subsequent additions
            const nextIndex = Math.max(...[...phases, ...addedPhases].map((p) => p.phaseIndex)) + 1;
            setNewPhase({ phaseBuildingType: "Job Tracking", phaseIndex: nextIndex, laborRate: "", materialMarkup: "" });

        } catch (error) {
            console.error("Error adding phase:", error);
        }
    };

    // Handle opening the Manage Devices Modal
    const handleOpenManageDevices = (phase) => {
        setSelectedPhase(phase);
        setIsManageDevicesOpen(true);
    };

    const handleOpenWorkItems = (phase) => {
        setSelectedPhase(phase);
        setIsManageWorkItemsOpen(true);
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{viewOnly ? "View" : "Manage"} Work Phases for Proposal #{proposalNumber}</DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <p>Loading...</p>
                ) : (
                    <>
                        {!viewOnly &&
                            <>
                                {isAddingPhase ? (
                                    <div className="bg-gray-100 p-4 rounded-lg mb-4">
                                        <label className="block mb-2">Phase #:</label>
                                        <input
                                            type="number"
                                            name="phaseIndex"
                                            value={newPhase.phaseIndex}
                                            disabled
                                            className="w-full p-2 border border-gray-300 rounded mb-2 bg-gray-200 cursor-not-allowed"
                                        />
                                        <label className="block mb-2">Building Type:</label>
                                        <Select
                                            name="phaseBuildingType"
                                            value={newPhase.phaseBuildingType}
                                            onChange={handleInputChange}
                                            fullWidth
                                            className="mb-2"
                                        >
                                            {buildingTypeOptions.map((option) => (
                                                <MenuItem key={option.key} value={option.key}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        <label className="block mb-2">Labor Rate:</label>
                                        <input
                                            type="number"
                                            name="laborRate"
                                            value={newPhase.laborRate}
                                            onChange={handleInputChange}
                                            className="w-full p-2 border border-gray-300 rounded mb-2"
                                        />

                                        <label className="block mb-2">Material Markup:</label>
                                        <input
                                            type="number"
                                            name="materialMarkup"
                                            value={newPhase.materialMarkup}
                                            onChange={handleInputChange}
                                            className="w-full p-2 border border-gray-300 rounded mb-2"
                                        />

                                        <Button variant="contained" color="success" onClick={handleAddPhase}>
                                            Save Phase
                                        </Button>
                                        <Button
                                            onClick={() => setIsAddingPhase(!isAddingPhase)}
                                            variant="contained"
                                            color="primary"
                                            sx={{ marginLeft: "10px" }}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                ) : (
                                    <Button
                                        onClick={() => setIsAddingPhase(!isAddingPhase)}
                                        variant="contained"
                                        color="primary"
                                        sx={{ marginBottom: "10px" }}
                                    >
                                        Add New Phase
                                    </Button>
                                )}
                            </>
                        }

                        {/* Work Phases Table */}
                        <table className="w-full border-collapse border border-gray-300">
                            <thead>
                                <tr className="bg-gray-200">
                                    <th className="border p-2">Phase #</th>
                                    <th className="border p-2">Building Type</th>
                                    <th className="border p-2">Labor Rate $</th>
                                    <th className="border p-2">Material Markup %</th>
                                    <th className="border p-2">{viewOnly ? "View" : "Manage"} Devices</th>
                                    <th className="border p-2">{viewOnly ? "View" : "Manage"} Work Items</th>
                                </tr>
                            </thead>
                            <tbody>
                                {phases.map((phase) => (
                                    <tr key={phase.phaseId} className="border">
                                        <td className="border p-2">{phase.phaseIndex}</td>
                                        <td className="border p-2">{phase.phaseBuildingType}</td>
                                        <td className="border p-2">{phase.laborRate}</td>
                                        <td className="border p-2">{phase.materialMarkup}</td>
                                        <td className="border p-2">
                                            <Button variant="outlined" color="secondary" onClick={() => handleOpenManageDevices(phase)}>
                                                {viewOnly ? "View" : "Manage"} Devices
                                            </Button>
                                        </td>
                                        <td className="border p-2">
                                            <Button variant="outlined" color="secondary" onClick={() => handleOpenWorkItems(phase)}>
                                                {viewOnly ? "View" : "Manage"} Work Items
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Close
                </Button>
            </DialogActions>

            <ManageDevicesModal open={isManageDevicesOpen} handleClose={() => setIsManageDevicesOpen(false)} phaseId={selectedPhase?.phaseId} allBuildingDevices={allBuildingDevices} viewOnly={viewOnly} />
            <ManageWorkItemsModal open={isManageWorkItemsOpen} handleClose={() => setIsManageWorkItemsOpen(false)} phaseId={selectedPhase?.phaseId} allWorkItems={allWorkItems} setAllWorkItems={setAllWorkItems} phaseIndex={selectedPhase?.phaseIndex} viewOnly={viewOnly} />
        </Dialog>
    );
};

export default WorkPhaseModal;
